'use client';

import { useTranslations } from 'next-intl';
import type {
  InternalNetworkAdEntityKind,
  InternalNetworkAssetSummaryDto,
} from '@terra/shared-contracts-internal-network';
import {
  ENTITY_KIND_COLOR_MAP,
  GRAPH_NODE_ICON,
} from '@terra/ui-graphs/internal-network';
import { HStack } from '@terra/ui-styled-system';
import { Badge, BodyDefault } from '@terra/ui-design-system';
import {
  ComputerIcon,
  HierarchyIcon,
  ServerStack01Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import { useDateFormatter } from '../../../../hooks/useDateFormatter';
import { ListPageEmptyCell } from '../../../common/ListPageEmptyCell';
import { ListPageNameCell } from '../../../common/ListPageNameCell';
import { AssetRowIcon } from '../AssetRowIcon';
import type { InternalAssetGridItem } from './internal-assets-table.model';

export function getCanonicalInternalAssetKindIcon(
  assetKind: InternalNetworkAssetSummaryDto['assetKind'],
): TerraIcon {
  if (assetKind === 'DOMAIN_CONTROLLER') {
    return ServerStack01Icon;
  }

  if (assetKind === 'COMPUTER' || assetKind === 'SERVER') {
    return ComputerIcon;
  }

  return HierarchyIcon;
}

export function InternalAssetNameCell({
  item,
}: {
  item: InternalAssetGridItem;
}) {
  const Icon =
    item.source === 'ad'
      ? GRAPH_NODE_ICON[item.entity.entityKind]
      : getCanonicalInternalAssetKindIcon(item.asset.assetKind);

  return (
    <ListPageNameCell icon={<AssetRowIcon icon={Icon} />} title={item.label} />
  );
}

export function InternalAdEntityKindBadge({
  entityKind,
}: {
  entityKind: InternalNetworkAdEntityKind;
}) {
  const tInternalNetwork = useTranslations('internalNetwork');

  return (
    <Badge
      variant="outline"
      size="sm"
      colorPalette={ENTITY_KIND_COLOR_MAP[entityKind]}
    >
      {tInternalNetwork(
        `crownJewels.entityKinds.${entityKind}` as Parameters<
          typeof tInternalNetwork
        >[0],
      )}
    </Badge>
  );
}

export function InternalAssetFlagCell({
  isSet,
  label,
  colorPalette,
}: {
  isSet: boolean;
  label: string;
  colorPalette: 'orange' | 'blue';
}) {
  return isSet ? (
    <Badge variant="fill" size="sm" colorPalette={colorPalette}>
      {label}
    </Badge>
  ) : (
    <ListPageEmptyCell />
  );
}

export function InternalAssetTextCell({ value }: { value: string | null }) {
  return value ? (
    <BodyDefault color="fg.neutral.muted" truncate title={value}>
      {value}
    </BodyDefault>
  ) : (
    <ListPageEmptyCell />
  );
}

export function InternalAssetDateCell({ value }: { value: string | null }) {
  const dateFormatter = useDateFormatter();

  return value ? (
    <BodyDefault color="fg.neutral.muted" truncate>
      {dateFormatter.format(new Date(value))}
    </BodyDefault>
  ) : (
    <ListPageEmptyCell />
  );
}

export function InternalCanonicalAssetKindBadge({
  asset,
}: {
  asset: InternalNetworkAssetSummaryDto;
}) {
  const tAssetList = useTranslations('internalNetwork.assetList');

  return (
    <Badge variant="outline" size="sm" colorPalette="gray">
      {tAssetList(`assetKind.${asset.assetKind}`)}
    </Badge>
  );
}

export function InternalCanonicalAssetStatusCell({
  asset,
}: {
  asset: InternalNetworkAssetSummaryDto;
}) {
  const tAssetList = useTranslations('internalNetwork.assetList');

  return (
    <HStack gap={1} minW={0}>
      <Badge
        variant="outline"
        size="sm"
        colorPalette={asset.presenceStatus === 'ONLINE' ? 'green' : 'gray'}
      >
        {tAssetList(`presenceStatus.${asset.presenceStatus}`)}
      </Badge>
      {asset.conflictCount > 0 ? (
        <Badge variant="outline" size="sm" colorPalette="orange">
          {tAssetList(`resolutionState.${asset.resolutionState}`)}
        </Badge>
      ) : null}
    </HStack>
  );
}
